import { apiRequest, getStoredToken } from './request';
import { throwOnNotOk } from './request';
import { getPreferredApiLocale } from './locale';
import { resolveStorageUrl } from '../utils/storageUrl';

export { AppError, getStoredToken } from './request';

const API_BASE = process.env.REACT_APP_API_URL ?? 'http://localhost:8080';
const WORKER_API_BASE = process.env.REACT_APP_WORKER_URL ?? 'http://localhost:8081';

/** Direct fetch helpers: Authorization + Accept-Language (apiRequest already sets these). */
function authLocaleHeaders(extra?: HeadersInit): HeadersInit {
  const headers: Record<string, string> = {
    'Accept-Language': getPreferredApiLocale(),
  };
  const token = getStoredToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (extra) Object.assign(headers, extra);
  return headers;
}

// --- Auth & Profile ---
export interface AuthUser {
  id: string;
  email: string;
  plan?: string;
  createdAt: string;
  subscriptionStatus?: string;
  subscriptionRenewsAt?: string;
  aiCredits?: {
    balance: number;
    grantedMonth: string;
  };
  trialUsedAt?: string;
  canUseAI?: boolean;
  hasProSubscription?: boolean;
  canCancelSubscription?: boolean;
  canReactivateSubscription?: boolean;
}

export interface AuthResponse {
  token: string;
  expiresAt: string;
  user: AuthUser;
}

export async function register(email: string, password: string): Promise<AuthResponse> {
  const res = await apiRequest<AuthResponse>(`${API_BASE}/api/v1/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res) throw new Error('Kayıt yanıtı alınamadı');
  return res;
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const res = await apiRequest<AuthResponse>(`${API_BASE}/api/v1/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res) throw new Error('Giriş yanıtı alınamadı');
  return res;
}

export async function getMe(): Promise<AuthUser> {
  const res = await apiRequest<AuthUser>(`${API_BASE}/api/v1/auth/me`);
  if (!res) throw new Error('Kullanıcı bilgisi alınamadı');
  return res;
}

/** Şifre değiştirir. Mevcut şifre + yeni şifre gerekir. */
export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/auth/me/password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      currentPassword,
      newPassword,
    }),
  });
}

/** Pro aboneliğini iptal eder. Dönem sonuna kadar erişim devam eder. */
export async function cancelSubscription(): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/billing/subscription/cancel`, {
    method: 'POST',
  });
}

export type FeedbackRating = 'dislike' | 'neutral' | 'like';

/** Panel feedback — write-only; no list/get API. */
export async function submitFeedback(input: {
  rating: FeedbackRating;
  message?: string;
  pagePath?: string;
}): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      rating: input.rating,
      message: input.message ?? '',
      pagePath: input.pagePath ?? '',
    }),
  });
}

/** İptal edilmiş Pro aboneliğini (süresi dolmadan) yeniden aktif eder. Güncel kullanıcıyı döner. */
export async function resumeSubscription(): Promise<AuthUser> {
  const user = await apiRequest<AuthUser>(`${API_BASE}/api/v1/billing/subscription/resume`, {
    method: 'POST',
  });
  if (!user) throw new Error('Kullanıcı bilgisi alınamadı');
  return user;
}

export interface CreditPack {
  id: string;
  variantId?: string;
  credits: number;
}

export interface CreditsBalanceResponse {
  balance: number;
  grantedMonth: string;
  currentMonth: string;
  grantedThisMonth: boolean;
  packs: CreditPack[];
}

export interface AICreditLedgerEntry {
  id: string;
  userId: string;
  createdAt: string;
  type: string;
  opType?: string;
  amount: number;
  balanceAfter: number;
  multiplier?: number;
  clientIp: string;
  userAgent: string;
  jobId?: string;
  applicationId?: string;
  ref?: string;
}

export async function getCreditsBalance(): Promise<CreditsBalanceResponse> {
  const res = await apiRequest<CreditsBalanceResponse>(`${API_BASE}/api/v1/credits`);
  if (!res) throw new Error('Credits balance alınamadı');
  return res;
}

export async function getCreditsHistory(params?: { before?: string; limit?: number }): Promise<{ items: AICreditLedgerEntry[] }> {
  const q = new URLSearchParams();
  if (params?.before) q.set('before', params.before);
  if (params?.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  const res = await apiRequest<{ items: AICreditLedgerEntry[] }>(
    `${API_BASE}/api/v1/credits/history${qs ? `?${qs}` : ''}`
  );
  return res ?? { items: [] };
}

export async function createCreditsCheckout(packId: string): Promise<string> {
  const res = await apiRequest<{ url: string }>(`${API_BASE}/api/v1/billing/credits/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ packId }),
  });
  if (!res?.url) throw new Error('Checkout URL alınamadı');
  return res.url;
}

/** Lemon Squeezy Pro checkout. Returns checkout URL; redirect user to it. */
export async function createCheckout(billing: 'monthly' | 'yearly'): Promise<{ url: string }> {
  const res = await apiRequest<{ url: string }>(`${API_BASE}/api/v1/billing/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ billing }),
  });
  if (!res?.url) throw new Error('Checkout URL alınamadı');
  return res;
}

export interface Application {
  id: string;
  userId?: string;
  type: string;
  name: string;
  description: string;
  summary: string;
  iosBundleId?: string;
  androidBundleId?: string;
  appStoreSecretId?: string;
  appStoreIapSecretId?: string;
  playStoreSecretId?: string;
  githubSecretId?: string;
  instagramSecretId?: string;
  instagramUsername?: string;
  instagramBusinessAccountId?: string;
  revenueCatSecretId?: string;
  revenueCatProjectId?: string;
  revenueCatIosAppId?: string;
  revenueCatAndroidAppId?: string;
  revenueCatLastProductSyncAt?: string;
  revenueCatLastProductSync?: {
    created: number;
    skipped: number;
    failed: number;
    packagesLinked?: number;
  };
  revenueCatEntitlementId?: string;
  revenueCatOfferingId?: string;
  revenueCatSetupCompletedAt?: string;
  appStoreAppId?: string;
  /** GitHub Pages (veya diğer) sayfa hosting durumu. */
  pageHosting?: PageHosting;
  iconPath?: string;
  iconUpdatedAt?: string;
  iconMimeType?: string;
  /** Marka renk paleti (primary, secondary, accent, background, text). */
  colorPalette?: ColorPalette;
  /** Platform bazlı fiyatlandırma (ios, android); uygulama seviyesi. */
  pricing?: Record<string, PlatformPricing>;
  /** Platform bazlı availability (ios, android); uygulama seviyesi. */
  availability?: Record<string, PlatformAvailability>;
  primaryLanguage?: string;
  copyright?: string;
  primaryCategory?: string;
  secondaryCategory?: string;
  contentRightsDeclaration?: string;
  ageRatingProfile?: string;
  reviewContactUserId?: string;
  reviewNotes?: string;
  /** apple = Apple Standard EULA; custom = one app-level custom agreement */
  eulaType?: 'apple' | 'custom';
  eulaUrl?: string;
  /** Plain-text custom EULA for App Store Connect agreementText */
  eulaText?: string;
  /** Soft uyarılar (ASC name conflict vb.); app detail buradan beslenir. */
  warnings?: AppWarning[];
  createdAt: string;
  updatedAt: string;
}

/** Uygulama / job soft uyarısı (örn. ASC_NAME_IN_USE). */
export interface AppWarning {
  id: string;
  code: string;
  message: string;
  locale?: string;
  field?: string;
  platform?: string;
  versionId?: string;
  jobId?: string;
  createdAt: string;
}

export interface PageHosting {
  provider: string;
  owner: string;
  repo: string;
  branch: string;
  docsPath: string;
  enabled: boolean;
  lastDeployedAt?: string;
  urls?: Record<string, string>;
}

export interface ColorPalette {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
}

export interface CreateApplicationInput {
  type: string;
  name: string;
  description: string;
  summary?: string;
  iosBundleId?: string;
  androidBundleId?: string;
  appStoreSecretId?: string;
  appStoreIapSecretId?: string;
  playStoreSecretId?: string;
}

export interface UpdateApplicationInput {
  type?: string;
  name?: string;
  description?: string;
  summary?: string;
  iosBundleId?: string;
  androidBundleId?: string;
  appStoreSecretId?: string;
  appStoreIapSecretId?: string;
  playStoreSecretId?: string;
  githubSecretId?: string;
  primaryLanguage?: string;
  copyright?: string;
  primaryCategory?: string;
  secondaryCategory?: string;
  contentRightsDeclaration?: string;
  ageRatingProfile?: string;
  reviewContactUserId?: string;
  reviewNotes?: string;
  eulaType?: 'apple' | 'custom';
  eulaUrl?: string;
  eulaText?: string;
  colorPalette?: ColorPalette;
  pricing?: Record<string, PlatformPricing>;
  availability?: Record<string, PlatformAvailability>;
}

export async function listApplications(): Promise<Application[]> {
  return apiRequest<Application[]>(`${API_BASE}/api/v1/applications`);
}

export async function createApplication(
  input: CreateApplicationInput
): Promise<Application> {
  return apiRequest<Application>(`${API_BASE}/api/v1/applications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function getApplication(id: string): Promise<Application> {
  return apiRequest<Application>(`${API_BASE}/api/v1/applications/${id}`);
}

export async function updateApplication(
  id: string,
  input: UpdateApplicationInput
): Promise<Application> {
  return apiRequest<Application>(`${API_BASE}/api/v1/applications/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/** App Store Connect'teki IAP + abonelikleri çeker ve iOS pricing.inAppProducts'a aktarır. */
export async function syncInAppPurchasesFromAppStore(appId: string): Promise<{
  application: Application;
  productsSynced: number;
  productsAdded: number;
}> {
  return apiRequest<{
    application: Application;
    productsSynced: number;
    productsAdded: number;
  }>(`${API_BASE}/api/v1/applications/${encodeURIComponent(appId)}/pricing/sync-iap`, {
    method: 'POST',
  });
}

export interface PushInAppPurchasesResult {
  created: number;
  existing?: number;
  skipped: number;
  failed: number;
  failures?: Array<{ productId: string; message: string }>;
  message: string;
}

/** Sistemdeki ürünleri App Store Connect'te oluşturur. productIds boşsa tüm eksikler. */
export async function pushInAppPurchasesToAppStore(
  appId: string,
  productIds?: string[]
): Promise<PushInAppPurchasesResult> {
  return apiRequest<PushInAppPurchasesResult>(
    `${API_BASE}/api/v1/applications/${encodeURIComponent(appId)}/pricing/push-iap`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productIds: productIds ?? [] }),
    }
  );
}

/** Seçilen IAP ürünlerinin fiyat/lokalizasyonlarını App Store Connect'e yükleyen job oluşturur. */
export async function createIAPPricingUploadJob(
  appId: string,
  productIds: string[]
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${encodeURIComponent(appId)}/pricing/iap-upload-job`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productIds }),
    }
  );
}

/** Soft uyarıyı uygulamadan kaldırır (düzeltme sonrası). */
export async function dismissApplicationWarning(
  appId: string,
  warningId: string
): Promise<Application> {
  return apiRequest<Application>(
    `${API_BASE}/api/v1/applications/${encodeURIComponent(appId)}/warnings/${encodeURIComponent(warningId)}`,
    { method: 'DELETE' }
  );
}

export interface ColorPaletteSuggestion {
  colorPalette: ColorPalette;
  extractedColors?: string[];
}

/** İkon renk analizi + AI marka paleti önerisi (kaydetmez; kabul sonrası updateApplication). */
export async function suggestColorPaletteFromIcon(
  appId: string
): Promise<ColorPaletteSuggestion> {
  return apiRequest<ColorPaletteSuggestion>(
    `${API_BASE}/api/v1/applications/${encodeURIComponent(appId)}/suggest-color-palette`,
    { method: 'POST' }
  );
}

export async function deleteApplication(id: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/applications/${id}`, {
    method: 'DELETE',
  });
}

export async function getInstagramOAuthURL(applicationId: string): Promise<{ url: string }> {
  const res = await apiRequest<{ url: string }>(
    `${API_BASE}/api/v1/applications/${applicationId}/instagram/oauth-url`
  );
  if (!res?.url) throw new Error('Instagram OAuth URL alınamadı');
  return res;
}

export async function disconnectInstagram(applicationId: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/applications/${applicationId}/instagram`, {
    method: 'DELETE',
  });
}

/** Uygulama ikonu URL'i (Authorization gerektirir). */
export function getApplicationIconUrl(appId: string, params?: { download?: boolean; version?: number }): string {
  let url = `${API_BASE}/api/v1/applications/${encodeURIComponent(appId)}/icon`;
  const qs: string[] = [];
  if (params?.download) qs.push('download=1');
  if (params?.version != null) qs.push(`v=${encodeURIComponent(String(params.version))}`);
  if (qs.length > 0) url += `?${qs.join('&')}`;
  return url;
}

/** Uygulama ikonunu Authorization ile blob olarak çeker (önizleme). */
export async function getApplicationIconAsBlob(appId: string, version?: number): Promise<Blob> {
  const url = getApplicationIconUrl(appId, { version });
  const res = await fetch(url, { headers: authLocaleHeaders() });
  if (!res.ok) await throwOnNotOk(res);
  return res.blob();
}

/** Uygulama ikonunu PNG olarak indirir (CDN path varsa doğrudan, yoksa API). */
export async function downloadApplicationIcon(
  appId: string,
  filename: string,
  iconPath?: string
): Promise<void> {
  let blob: Blob;
  if (iconPath?.trim()) {
    const res = await fetch(resolveStorageUrl(iconPath));
    if (!res.ok) throw new Error(`İkon indirilemedi (${res.status})`);
    blob = await res.blob();
  } else {
    const url = getApplicationIconUrl(appId, { download: true });
    const res = await fetch(url, { headers: authLocaleHeaders() });
    if (!res.ok) await throwOnNotOk(res);
    blob = await res.blob();
  }
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename.endsWith('.png') ? filename : `${filename}.png`;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

/** Multipart form ile uygulama ikonu yükler. */
export async function uploadApplicationIcon(appId: string, file: File): Promise<Application> {
  const form = new FormData();
  form.append('icon', file);
  const res = await fetch(`${API_BASE}/api/v1/applications/${encodeURIComponent(appId)}/icon`, {
    method: 'POST',
    headers: authLocaleHeaders(),
    body: form,
  });
  if (!res.ok) await throwOnNotOk(res);
  return res.json() as Promise<Application>;
}

/** Uygulama ikonunu siler. */
export async function deleteApplicationIcon(appId: string): Promise<Application> {
  return apiRequest<Application>(
    `${API_BASE}/api/v1/applications/${encodeURIComponent(appId)}/icon`,
    { method: 'DELETE' }
  );
}

export interface StorageUploadResult {
  path: string;
  url: string;
  mimeType: string;
  size: number;
}

/** Multipart form ile object storage'a dosya yükler (bucket: app-screens vb.). */
export async function uploadStorageFile(bucket: string, file: File): Promise<StorageUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}/api/v1/storage/${encodeURIComponent(bucket)}/upload`, {
    method: 'POST',
    headers: authLocaleHeaders(),
    body: form,
  });
  if (!res.ok) await throwOnNotOk(res);
  return res.json() as Promise<StorageUploadResult>;
}

// BM-4: Mobil app version meta — API response platform bazlı localizations kullanır.
export interface PlatformLocalization {
  nameLocalized: Record<string, string>;
  subtitleLocalized: Record<string, string>;
  promotionalTextLocalized: Record<string, string>;
  descriptionLocalized: Record<string, string>;
  keywordsLocalized: Record<string, string[]>;
  releaseNotesLocalized?: Record<string, string>;
}

/** Uygulama içi satın alma: tek seferlik veya abonelik. Lokalizasyon gibi locale bazlı isim/açıklama ve fiyat. */
export interface InAppProduct {
  productId: string;
  type: 'one_time' | 'subscription';
  nameLocalized: Record<string, string>;
  descriptionLocalized: Record<string, string>;
  priceTierByLocale: Record<string, string>;
  /** RegionPriceTierT2..T4 -> fiyat değeri; T1 priceTierByLocale'den gelir. */
  priceByRegionTier?: Record<string, string>;
  subscriptionDuration?: string;
  trialDays?: number;
  /** First-year introductory offer price tier by locale; subscription only. */
  introPriceTierByLocale?: Record<string, string>;
  /** True after a successful store upload or a pull from that store. New products start false. */
  uploaded?: boolean;
  /** Ready-to-display store upload status (from API). */
  uploadedStatusLabel?: string;
  /** User-managed flag: whether this product is currently shown / actively on sale in the app. */
  activeOnSale?: boolean;
}

/** Tek platform (ios/android) için fiyatlandırma: uygulama ücreti (locale bazlı) + uygulama içi ürünler. */
export interface PlatformPricing {
  /** Girilen tutarın para birimi (diğer locale hesaplaması için referans). */
  appPriceCurrency?: string;
  appPriceTierByLocale: Record<string, string>;
  /** RegionPriceTierT2..T4 -> fiyat değeri; T1 appPriceTierByLocale'den gelir. */
  appPriceByRegionTier?: Record<string, string>;
  inAppProducts: InAppProduct[];
}

/** Tek platform (ios/android) için mağaza dağıtım ülkeleri/bölgeleri. */
export interface PlatformAvailability {
  mode: 'all' | 'selected';
  territories?: string[];
  availableInNewTerritories?: boolean;
  includeRestOfWorld?: boolean;
}

export interface AppVersion {
  id: string;
  applicationId: string;
  version: string;
  supportUrl: string;
  marketingUrl: string;
  privacyUrl: string;
  privacyChoicesUrl: string;
  /** Platform bazlı çeviriler (ios, android). API bu yapıyı döner. */
  localizations?: Record<string, PlatformLocalization>;
  /** Eski API: düz nameLocalized vb.; localizations yoksa kullanılır, UI normalleştirir. */
  nameLocalized?: Record<string, string>;
  subtitleLocalized?: Record<string, string>;
  promotionalTextLocalized?: Record<string, string>;
  descriptionLocalized?: Record<string, string>;
  keywordsLocalized?: Record<string, string[]>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVersionInput {
  version: string;
  supportUrl?: string;
  marketingUrl?: string;
  privacyUrl?: string;
  privacyChoicesUrl?: string;
  /** Platform bazlı gönderim (API kabul ediyorsa). */
  localizations?: Record<string, PlatformLocalization>;
  nameLocalized?: Record<string, string>;
  subtitleLocalized?: Record<string, string>;
  promotionalTextLocalized?: Record<string, string>;
  descriptionLocalized?: Record<string, string>;
  keywordsLocalized?: Record<string, string[]>;
}

export interface CopyVersionInput {
  version: string;
  releaseNotes: string;
}

/** Version kopyalama yanıtı; What's New girildiyse translationJobId dönebilir. */
export interface CopyVersionResult extends AppVersion {
  translationJobId?: string;
}

export async function listVersions(appId: string): Promise<AppVersion[]> {
  return apiRequest<AppVersion[]>(`${API_BASE}/api/v1/applications/${appId}/versions`);
}

export async function getVersion(
  appId: string,
  versionId: string
): Promise<AppVersion> {
  return apiRequest<AppVersion>(
    `${API_BASE}/api/v1/applications/${appId}/versions/${versionId}`
  );
}

export async function createVersion(
  appId: string,
  input: CreateVersionInput
): Promise<AppVersion> {
  return apiRequest<AppVersion>(
    `${API_BASE}/api/v1/applications/${appId}/versions`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }
  );
}

export async function updateVersion(
  appId: string,
  versionId: string,
  input: Partial<CreateVersionInput>
): Promise<AppVersion> {
  return apiRequest<AppVersion>(
    `${API_BASE}/api/v1/applications/${appId}/versions/${versionId}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }
  );
}

export async function deleteVersion(
  appId: string,
  versionId: string
): Promise<void> {
  await apiRequest<void>(
    `${API_BASE}/api/v1/applications/${appId}/versions/${versionId}`,
    { method: 'DELETE' }
  );
}

/** Mevcut version'ı kopyalayarak yeni version oluşturur. What's New doluysa translationJobId dönebilir. */
export async function copyVersion(
  appId: string,
  sourceVersionId: string,
  input: CopyVersionInput
): Promise<CopyVersionResult> {
  return apiRequest<CopyVersionResult>(
    `${API_BASE}/api/v1/applications/${appId}/versions/${sourceVersionId}/copy`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }
  );
}

export interface GenerateLocalizationOutput {
  name: string;
  subtitle: string;
  promotionalText: string;
  description: string;
  /** Virgülle ayrılmış string; API bu formatta döner. */
  keywords: string;
  releaseNotes?: string;
}

export async function generateVersionLocalization(
  appId: string,
  versionId: string,
  locale: string,
  platform?: string
): Promise<GenerateLocalizationOutput> {
  return apiRequest<GenerateLocalizationOutput>(
    `${API_BASE}/api/v1/applications/${appId}/versions/${versionId}/generate-localization`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ locale, platform: platform ?? PLATFORM_IOS }),
    }
  );
}

/** BM-7: Version'ın primary dilindeki veriyi AI ile hedef dillere çevirir. targetLocales boş ise primary hariç tüm dillere çevrilir. */
export async function generateVersionLocalizations(
  appId: string,
  versionId: string,
  targetLocales?: string[]
): Promise<AppVersion> {
  return apiRequest<AppVersion>(
    `${API_BASE}/api/v1/applications/${appId}/versions/${versionId}/generate-localizations`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetLocales: targetLocales ?? [] }),
    }
  );
}

/** Metadata export-only job (DebugMode). Normal kullanıcılar createMetadataUploadJob kullanır. */
export async function createMetadataExportJob(
  appId: string,
  versionId: string,
  targetLocales?: string[],
  platforms?: string[]
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${appId}/versions/${versionId}/export-metadata`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        targetLocales: targetLocales ?? [],
        platforms: platforms ?? [],
      }),
    }
  );
}

/** Metadata translation (AI çeviri) job'ı oluşturur. platforms: ["ios"], ["android"] veya ikisi. onlyReleaseNotes: yalnızca What's New. */
export async function createMetadataTranslationJob(
  appId: string,
  versionId: string,
  targetLocales?: string[],
  platforms?: string[],
  onlyReleaseNotes?: boolean
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${appId}/versions/${versionId}/translate-metadata`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        targetLocales: targetLocales ?? [],
        platforms: platforms ?? [],
        onlyReleaseNotes: onlyReleaseNotes ?? false,
      }),
    }
  );
}

/** AI key=value çeviri job'ı oluşturur ve arka planda başlatır. Tamamlanınca artifact'lar job dizinine yazılır; zip /jobs/:id/artifacts/archive ile indirilir. */
export async function createAITranslationJob(body: {
  content: string;
  sourceLocale: string;
  targetLocales: string[];
}): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(`${API_BASE}/api/v1/ai-translation/jobs`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Senkron AI key=value çeviri. results[locale] = key=value metni. */
export async function translateKeyValue(body: {
  content: string;
  sourceLocale: string;
  targetLocales: string[];
}): Promise<{ results: Record<string, string> }> {
  return apiRequest<{ results: Record<string, string> }>(`${API_BASE}/api/v1/ai-translation/translate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Metadata upload job'ı oluşturur (export + mağaza yükleme). platforms: ["ios"], ["android"] veya ikisi; boş = ikisi. */
export async function createMetadataUploadJob(
  appId: string,
  versionId: string,
  platforms?: string[],
  targetLocales?: string[]
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${appId}/versions/${versionId}/metadata-upload`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platforms: platforms ?? [],
        targetLocales: targetLocales ?? [],
      }),
    }
  );
}

/** Publish grup job'ı oluşturur (metadata_upload → ops. screens). Dönen jobId ile triggerJob çağırın. */
export async function createPublishJob(
  appId: string,
  versionId: string,
  platforms: string[],
  includeScreens?: boolean,
  opts?: { localizeAppPreviews?: boolean }
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${appId}/versions/${versionId}/publish`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        platforms: platforms ?? [],
        includeScreens: Boolean(includeScreens),
        ...(opts?.localizeAppPreviews ? { localizeAppPreviews: true } : {}),
      }),
    }
  );
}

export const PLATFORM_IOS = 'ios';
export const PLATFORM_ANDROID = 'android';

/** App Store metadata upload öncesi eksik alanları döner (issue key listesi). */
export function validateIOSMetadataUploadReadiness(
  app: Application | null | undefined,
  version: AppVersion | null | undefined
): string[] {
  const issues: string[] = [];
  if (!app || !version) return issues;
  if (!app.reviewContactUserId?.trim()) issues.push('reviewContactUser');
  if (!app.iosBundleId?.trim()) issues.push('iosBundleId');
  const pricing = app.pricing?.[PLATFORM_IOS];
  if (!pricing?.appPriceTierByLocale || Object.keys(pricing.appPriceTierByLocale).length === 0) {
    issues.push('iosPricing');
  }
  const av = app.availability?.[PLATFORM_IOS];
  if (!av) {
    issues.push('iosAvailability');
  } else   if (av.mode === 'selected' && (!av.territories || av.territories.length === 0)) {
    issues.push('iosAvailabilityTerritories');
  }
  if (app.eulaType === 'custom' && !(app.eulaText ?? '').trim()) {
    issues.push('customEulaText');
  }
  if (!app.primaryCategory?.trim()) {
    issues.push('primaryCategory');
  }
  return issues;
}

/** Play Store metadata upload öncesi eksik alanları döner (issue key listesi). */
export function validateAndroidMetadataUploadReadiness(
  app: Application | null | undefined,
  version: AppVersion | null | undefined
): string[] {
  const issues: string[] = [];
  if (!app || !version) return issues;
  if (!app.androidBundleId?.trim()) issues.push('androidBundleId');
  const av = app.availability?.[PLATFORM_ANDROID];
  if (!av) {
    issues.push('androidAvailability');
  } else if (av.mode === 'selected' && (!av.territories || av.territories.length === 0)) {
    issues.push('androidAvailabilityCountries');
  }
  return issues;
}

/** Seçili platformlar için metadata upload hazırlık kontrolü. */
export function validateMetadataUploadReadiness(
  app: Application | null | undefined,
  version: AppVersion | null | undefined,
  platforms: string[]
): string[] {
  if (platforms.length === 0) return ['selectPlatform'];
  const issues: string[] = [];
  if (platforms.includes(PLATFORM_IOS)) {
    issues.push(...validateIOSMetadataUploadReadiness(app, version));
  }
  if (platforms.includes(PLATFORM_ANDROID)) {
    issues.push(...validateAndroidMetadataUploadReadiness(app, version));
  }
  return Array.from(new Set(issues));
}

// --- Team users (App Store review contacts) ---
export interface TeamUser {
  id: string;
  userId: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  demoAccountRequired: boolean;
  demoAccountUsername?: string;
  demoAccountPassword?: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTeamUserInput {
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  demoAccountRequired: boolean;
  demoAccountUsername?: string;
  demoAccountPassword?: string;
  isDefault?: boolean;
}

export interface UpdateTeamUserInput {
  label?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  demoAccountRequired?: boolean;
  demoAccountUsername?: string;
  demoAccountPassword?: string;
  isDefault?: boolean;
}

export async function listTeamUsers(): Promise<TeamUser[]> {
  return apiRequest<TeamUser[]>(`${API_BASE}/api/v1/team-users`);
}

export async function createTeamUser(input: CreateTeamUserInput): Promise<TeamUser> {
  return apiRequest<TeamUser>(`${API_BASE}/api/v1/team-users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function updateTeamUser(id: string, input: UpdateTeamUserInput): Promise<TeamUser> {
  return apiRequest<TeamUser>(`${API_BASE}/api/v1/team-users/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function deleteTeamUser(id: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/team-users/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// --- System details (DebugMode / goodmode only; enforced on API) ---
export interface SystemMembership {
  id: string;
  email: string;
  plan: string;
  effectivePlan: string;
  createdAt: string;
  updatedAt: string;
  subscriptionStatus?: string;
  subscriptionRenewsAt?: string;
  lemonSubscriptionId?: string;
}

export interface SystemPlan {
  id: string;
  name: string;
  debugMode: boolean;
  canUseAI: boolean;
  maxApplications: number;
}

export async function listSystemMemberships(): Promise<SystemMembership[]> {
  return apiRequest<SystemMembership[]>(`${API_BASE}/api/v1/system/memberships`);
}

export async function getSystemMembership(id: string): Promise<SystemMembership> {
  return apiRequest<SystemMembership>(`${API_BASE}/api/v1/system/memberships/${encodeURIComponent(id)}`);
}

export async function listSystemPlans(): Promise<SystemPlan[]> {
  return apiRequest<SystemPlan[]>(`${API_BASE}/api/v1/system/plans`);
}

export async function updateSystemMembershipPlan(id: string, plan: string): Promise<SystemMembership> {
  return apiRequest<SystemMembership>(
    `${API_BASE}/api/v1/system/memberships/${encodeURIComponent(id)}/plan`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan }),
    }
  );
}

export type SystemSubscriptionStatus = 'active' | 'expired' | 'cancelled';
export type SystemDurationUnit = 'week' | 'month';

export interface UpdateSystemSubscriptionInput {
  status: SystemSubscriptionStatus;
  durationAmount?: number;
  durationUnit?: SystemDurationUnit;
}

export async function updateSystemMembershipSubscription(
  id: string,
  input: UpdateSystemSubscriptionInput
): Promise<SystemMembership> {
  return apiRequest<SystemMembership>(
    `${API_BASE}/api/v1/system/memberships/${encodeURIComponent(id)}/subscription`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    }
  );
}

// BM-9: Appconnect Secret yönetimi
export const SECRET_TYPE_APP_STORE = 'app_store';
export const SECRET_TYPE_APP_STORE_IAP = 'app_store_iap';
export const SECRET_TYPE_PLAY_STORE = 'play_store';
export const SECRET_TYPE_GITHUB = 'github';
export const SECRET_TYPE_REVENUECAT = 'revenuecat';
export const SECRET_TYPE_APPLE_SEARCH_ADS = 'apple_search_ads';

export interface Secret {
  id: string;
  type: string;
  name: string;
  payload: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSecretInput {
  type: string;
  name: string;
  payload: Record<string, unknown>;
}

export interface UpdateSecretInput {
  name?: string;
  payload?: Record<string, unknown>;
}

/** BM-8: type verilirse sadece o tipteki secret'ları döner (dropdown için). */
export async function listSecrets(type?: string): Promise<Secret[]> {
  const url = type
    ? `${API_BASE}/api/v1/secrets?type=${encodeURIComponent(type)}`
    : `${API_BASE}/api/v1/secrets`;
  return apiRequest<Secret[]>(url);
}

export async function createSecret(input: CreateSecretInput): Promise<Secret> {
  return apiRequest<Secret>(`${API_BASE}/api/v1/secrets`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function getSecret(id: string): Promise<Secret> {
  return apiRequest<Secret>(`${API_BASE}/api/v1/secrets/${id}`);
}

export async function updateSecret(
  id: string,
  input: UpdateSecretInput
): Promise<Secret> {
  return apiRequest<Secret>(`${API_BASE}/api/v1/secrets/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function deleteSecret(id: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/secrets/${id}`, {
    method: 'DELETE',
  });
}

/** Ready-to-display delete confirmation (includes linked app info when present). */
export async function getSecretDeleteConfirm(id: string): Promise<{ message: string }> {
  return apiRequest<{ message: string }>(
    `${API_BASE}/api/v1/secrets/${encodeURIComponent(id)}/delete-confirm`
  );
}

/** GitHub OAuth authorize URL (Secrets → GitHub Auth). */
export async function getGitHubOAuthURL(): Promise<string> {
  const res = await apiRequest<{ url: string }>(`${API_BASE}/api/v1/secrets/github/oauth-url`);
  if (!res?.url) throw new Error('GitHub OAuth URL alınamadı');
  return res.url;
}

export interface GitHubOwnerChoice {
  login: string;
  type: 'User' | 'Organization' | string;
  avatarUrl?: string;
}

export interface GitHubRepoChoice {
  name: string;
  fullName?: string;
  defaultBranch?: string;
  htmlUrl?: string;
  private?: boolean;
}

/** Kişisel hesap + üye olunan org listesi (Pages owner seçimi). */
export async function listPageHostingOwners(
  applicationId: string,
  secretId?: string
): Promise<GitHubOwnerChoice[]> {
  const q = secretId ? `?secretId=${encodeURIComponent(secretId)}` : '';
  const res = await apiRequest<{ owners: GitHubOwnerChoice[] }>(
    `${API_BASE}/api/v1/applications/${applicationId}/page-hosting/owners${q}`
  );
  return res?.owners ?? [];
}

/** Seçilen owner altındaki erişilebilir repo listesi. */
export async function listPageHostingRepos(
  applicationId: string,
  opts?: { secretId?: string; owner?: string }
): Promise<GitHubRepoChoice[]> {
  const q = new URLSearchParams();
  if (opts?.secretId) q.set('secretId', opts.secretId);
  if (opts?.owner) q.set('owner', opts.owner);
  const qs = q.toString();
  const res = await apiRequest<{ repos: GitHubRepoChoice[] }>(
    `${API_BASE}/api/v1/applications/${applicationId}/page-hosting/repos${qs ? `?${qs}` : ''}`
  );
  return res?.repos ?? [];
}

/** Uygulama için GitHub Pages repo oluşturur / Pages açar. */
export async function enablePageHosting(
  applicationId: string,
  opts?: { provider?: string; owner?: string; repo?: string }
): Promise<Application> {
  return apiRequest<Application>(
    `${API_BASE}/api/v1/applications/${applicationId}/page-hosting/enable`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        provider: opts?.provider,
        owner: opts?.owner,
        repo: opts?.repo,
      }),
    }
  );
}

/** BuildMate PageHosting bağını kaldırır (GitHub reposu silinmez). */
export async function disablePageHosting(applicationId: string): Promise<Application> {
  return apiRequest<Application>(
    `${API_BASE}/api/v1/applications/${applicationId}/page-hosting`,
    { method: 'DELETE' }
  );
}

/** AI legal HTML üret + GitHub Pages deploy job (version URL'leri için). */
export async function createPageSiteDeployJob(
  applicationId: string,
  versionId: string,
  opts?: { pageTypes?: string[]; provider?: string }
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${applicationId}/versions/${versionId}/page-site-deploy`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pageTypes: opts?.pageTypes,
        provider: opts?.provider,
      }),
    }
  );
}

/** App-level page deploy (örn. yalnızca EULA). */
export async function createAppPageSiteDeployJob(
  applicationId: string,
  opts?: { pageTypes?: string[]; provider?: string }
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${applicationId}/page-site-deploy`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        pageTypes: opts?.pageTypes,
        provider: opts?.provider,
      }),
    }
  );
}


// Key/Value yapısını (BSON/Go'dan gelebilir) normal objeye çevirir.
function isKeyValueArray(arr: unknown): arr is { Key: string; Value: unknown }[] {
  return (
    Array.isArray(arr) &&
    arr.length > 0 &&
    arr.every(
      (x) =>
        x != null &&
        typeof x === 'object' &&
        'Key' in x &&
        'Value' in x
    )
  );
}

function normalizeKeyValuePayload(v: unknown): unknown {
  if (v == null) return v;
  if (Array.isArray(v)) {
    if (isKeyValueArray(v)) {
      return v.reduce(
        (acc, e) => ({ ...acc, [e.Key]: normalizeKeyValuePayload(e.Value) }),
        {} as Record<string, unknown>
      );
    }
    return v.map(normalizeKeyValuePayload);
  }
  if (typeof v === 'object') {
    return Object.fromEntries(
      Object.entries(v).map(([k, val]) => [k, normalizeKeyValuePayload(val)])
    );
  }
  return v;
}

// API'den gelen ekran objesi bazen { "0": {...}, "layoutItems": [...] } şeklinde olabilir; tek düz objeye indirger.
function flattenScreen(screen: unknown, index: number): unknown {
  if (screen == null || typeof screen !== 'object' || Array.isArray(screen)) {
    return screen;
  }
  const o = screen as Record<string, unknown>;
  const numericKey = Object.keys(o).find((k) => /^\d+$/.test(k));
  if (numericKey != null && ('layoutItems' in o || o[numericKey] != null)) {
    const base = (o[numericKey] as Record<string, unknown>) ?? {};
    return {
      ...base,
      layoutItems: o.layoutItems ?? base.layoutItems ?? [],
      id: o.id ?? base.id ?? `screen-${index}-${Date.now()}`,
    };
  }
  return screen;
}

// App Screens — bağımsız ekran setleri (AppVersion ile aynı değil).
export interface AppScreensSetSummary {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

export async function listAppScreensSets(appId: string): Promise<AppScreensSetSummary[]> {
  const data = await apiRequest<{ items?: AppScreensSetSummary[] }>(
    `${API_BASE}/api/v1/applications/${appId}/screens`
  );
  return Array.isArray(data?.items) ? data.items : [];
}

export async function createAppScreensSet(
  appId: string,
  input: { name: string; copyFromId?: string }
): Promise<AppScreensSetSummary> {
  return apiRequest<AppScreensSetSummary>(`${API_BASE}/api/v1/applications/${appId}/screens`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function deleteAppScreensSet(appId: string, screensId: string): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    `${API_BASE}/api/v1/applications/${appId}/screens/${encodeURIComponent(screensId)}`,
    { method: 'DELETE' }
  );
}

export interface AppScreenIssue {
  id: string;
  code: string;
  severity: 'error' | 'warning';
  message: string;
  guideline?: string;
  screenId?: string;
  screenIndex?: number;
  locale?: string;
  layoutItemId?: string;
}

export async function getAppScreens(
  appId: string,
  screensId: string
): Promise<{
  id?: string;
  name?: string;
  screens: unknown[];
  primaryLocale?: string;
  targetLocales: string[];
  completedTargetLocales: string[];
  exportWidth?: number;
  exportHeight?: number;
  issues: AppScreenIssue[];
}> {
  const data = await apiRequest<{
    id?: string;
    name?: string;
    screens?: unknown[] | { screens?: unknown[] };
    primaryLocale?: string;
    targetLocales?: string[];
    completedTargetLocales?: string[];
    exportWidth?: number;
    exportHeight?: number;
    issues?: AppScreenIssue[];
  }>(`${API_BASE}/api/v1/applications/${appId}/screens/${encodeURIComponent(screensId)}`);
  const raw =
    Array.isArray(data?.screens) ? data.screens : (data?.screens as { screens?: unknown[] })?.screens;
  const arr = Array.isArray(raw) ? raw : [];
  const normalized = arr
    .map((s) => normalizeKeyValuePayload(s))
    .map((s, i) => flattenScreen(s, i));
  const exportWidth = typeof data?.exportWidth === 'number' && data.exportWidth > 0 ? data.exportWidth : undefined;
  const exportHeight = typeof data?.exportHeight === 'number' && data.exportHeight > 0 ? data.exportHeight : undefined;
  return {
    id: typeof data?.id === 'string' ? data.id : undefined,
    name: typeof data?.name === 'string' ? data.name : undefined,
    screens: normalized,
    primaryLocale: typeof data?.primaryLocale === 'string' ? data.primaryLocale : undefined,
    targetLocales: Array.isArray(data?.targetLocales) ? data.targetLocales : [],
    completedTargetLocales: Array.isArray(data?.completedTargetLocales) ? data.completedTargetLocales : [],
    exportWidth,
    exportHeight,
    issues: Array.isArray(data?.issues) ? data.issues : [],
  };
}

export async function saveAppScreens(
  appId: string,
  screensId: string,
  payload: {
    screens?: unknown[];
    name?: string;
    primaryLocale?: string;
    targetLocales?: string[];
    exportWidth?: number;
    exportHeight?: number;
  }
): Promise<{ ok: boolean; issues: AppScreenIssue[] }> {
  const data = await apiRequest<{ ok?: boolean; issues?: AppScreenIssue[] }>(
    `${API_BASE}/api/v1/applications/${appId}/screens/${encodeURIComponent(screensId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  return {
    ok: data?.ok !== false,
    issues: Array.isArray(data?.issues) ? data.issues : [],
  };
}

/** Live App Store Guideline 2.3 review check (localized messages). */
export async function checkAppScreensReview(
  appId: string,
  screensId: string,
  payload?: {
    screens?: unknown[];
    primaryLocale?: string;
    targetLocales?: string[];
    completedTargetLocales?: string[];
  }
): Promise<AppScreenIssue[]> {
  const data = await apiRequest<{ issues?: AppScreenIssue[] }>(
    `${API_BASE}/api/v1/applications/${appId}/screens/${encodeURIComponent(screensId)}/review-check`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    }
  );
  return Array.isArray(data?.issues) ? data.issues : [];
}

/** Ekran yükleme job'ı oluşturur. appStoreVersion: ASC sürüm string'i (AppVersion metadata ile aynı şey değil). */
export async function createScreensUploadJob(
  appId: string,
  screensId: string,
  platforms: ('app_store' | 'play_store')[],
  opts?: { appStoreVersion?: string; localizeAppPreviews?: boolean; uploadAsABTest?: boolean }
): Promise<{ jobId: string }> {
  const body: {
    platforms: string[];
    appStoreVersion?: string;
    localizeAppPreviews?: boolean;
    uploadAsABTest?: boolean;
  } = { platforms };
  const v = opts?.appStoreVersion?.trim();
  if (v) body.appStoreVersion = v;
  if (opts?.localizeAppPreviews) body.localizeAppPreviews = true;
  if (opts?.uploadAsABTest) body.uploadAsABTest = true;
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${appId}/screens/${encodeURIComponent(screensId)}/upload`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
}

export async function createScreensTranslateJob(appId: string, screensId: string): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${appId}/screens/${encodeURIComponent(screensId)}/translate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
}

export async function suggestAppScreensAITemplate(
  appId: string,
  screensId: string,
  body: {
    primaryLocale?: string;
    contextOverride?: string;
    userPrompt?: string;
    referenceImageUrls?: string[];
  }
): Promise<{ screens: unknown[] }> {
  const data = await apiRequest<{ screens?: unknown[] }>(
    `${API_BASE}/api/v1/applications/${appId}/screens/${encodeURIComponent(screensId)}/ai-suggest-template`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  const raw = Array.isArray(data?.screens) ? data.screens : [];
  const normalized = raw.map(s => normalizeKeyValuePayload(s)).map((s, i) => flattenScreen(s, i));
  return { screens: normalized };
}

export async function redesignAppScreenAI(
  appId: string,
  screensId: string,
  body: {
    primaryLocale?: string;
    contextOverride?: string;
    userPrompt?: string;
    preserveScreenId?: string;
    screen: unknown;
  }
): Promise<{ screen: unknown }> {
  const data = await apiRequest<{ screen?: unknown }>(
    `${API_BASE}/api/v1/applications/${appId}/screens/${encodeURIComponent(screensId)}/ai-redesign`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  const s = data?.screen;
  if (s == null || typeof s !== 'object') {
    return { screen: null };
  }
  const normalized = flattenScreen(normalizeKeyValuePayload(s), 0);
  return { screen: normalized };
}

// App Screens AI chat — asistan mevcut (canlı editör) ekranlara hakim; yapıştırılan
// App Store Review geri bildirimleri için yazılı öneriler döner. Faz 1: sadece metin.
export interface AppScreensChatMessagePayload {
  role: 'user' | 'assistant';
  content: string;
}

export async function chatAppScreensAI(
  appId: string,
  screensId: string,
  body: {
    message: string;
    messages?: AppScreensChatMessagePayload[];
    reviewText?: string;
    screens?: unknown[];
    primaryLocale?: string;
    targetLocales?: string[];
    responseLanguage?: string;
  }
): Promise<{ reply: string }> {
  const data = await apiRequest<{ reply?: string }>(
    `${API_BASE}/api/v1/applications/${appId}/screens/${encodeURIComponent(screensId)}/ai-chat`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  return { reply: typeof data?.reply === 'string' ? data.reply : '' };
}

/** Pricing AI advisor — canlı (kaydedilmemiş) fiyat formu için tek ürün satırı. */
export interface PricingAdviceProductPayload {
  productId: string;
  type: 'one_time' | 'subscription';
  name?: string;
  priceTier?: string;
  subscriptionDuration?: string;
  trialDays?: number;
  activeOnSale?: boolean;
}

/**
 * Makine tarafından uygulanabilir fiyat önerisi. Apply aksiyonu bunu canlı fiyat formuna
 * uygular: productId mevcut bir ürünle eşleşirse günceller, eşleşmezse yeni ürün oluşturur.
 */
export interface PricingAdviceSuggestion {
  productId: string;
  type: 'one_time' | 'subscription';
  name?: string;
  /** Kaydedilen tier değeri: "free" | "tier_1".."tier_6" | "price_{n}_99". */
  priceTier?: string;
  subscriptionDuration?: string;
  trialDays?: number;
}

/**
 * Aktif satıştaki fiyatları AI ile değerlendirir; tier bazlı öneri metni + (varsa) yapısal
 * öneriler döner. Öneri metni UI dilinde (responseLanguage) üretilir; suggestions makine içindir.
 */
export async function suggestPricingAdvice(
  appId: string,
  body: {
    message: string;
    messages?: AppScreensChatMessagePayload[];
    platform: string;
    responseLanguage?: string;
    pricing?: {
      currency?: string;
      appPriceTier?: string;
      products: PricingAdviceProductPayload[];
    };
  }
): Promise<{ reply: string; suggestions: PricingAdviceSuggestion[] }> {
  const data = await apiRequest<{ reply?: string; suggestions?: PricingAdviceSuggestion[] }>(
    `${API_BASE}/api/v1/applications/${encodeURIComponent(appId)}/pricing/ai-advice`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  );
  return {
    reply: typeof data?.reply === 'string' ? data.reply : '',
    suggestions: Array.isArray(data?.suggestions) ? data.suggestions : [],
  };
}

// RevenueCat — paywall lokalizasyonu (App Screens ile aynı desen: import edilen paywall metinleri AI ile çevrilir, sonra RevenueCat'e geri yüklenir).
export interface RevenueCatRemotePaywall {
  id: string;
  offeringId?: string;
}

export interface RevenueCatTextEntry {
  key: string;
  sourceText: string;
  translations?: Record<string, string>;
  /** AI çevirisine dahil mi. Yoksa/true = çevrilir; false = kaynak metin hedef dillere aynen kopyalanır. */
  translate?: boolean;
}

export interface RevenueCatPaywall {
  id: string;
  applicationId: string;
  remotePaywallId: string;
  remoteVersionId?: string;
  offeringId?: string;
  name: string;
  entries: RevenueCatTextEntry[];
  primaryLocale: string;
  targetLocales: string[];
  completedTargetLocales: string[];
  active: boolean;
  autoManaged: boolean;
  lastSyncedAt?: string;
  lastUploadedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RevenueCatPaywallSummary {
  id: string;
  name: string;
  remotePaywallId: string;
  active: boolean;
  autoManaged: boolean;
  primaryLocale: string;
  targetLocales: string[];
  completedTargetLocales: string[];
  lastSyncedAt?: string;
  lastUploadedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** App ekranından API key + Project ID ile RevenueCat bağlantısı kurar (app'e özel secret oluşturulur). */
export async function connectRevenueCat(
  appId: string,
  apiKey: string,
  projectId: string
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`${API_BASE}/api/v1/applications/${appId}/revenuecat/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ apiKey, projectId }),
  });
}

export async function disconnectRevenueCat(appId: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/applications/${appId}/revenuecat`, { method: 'DELETE' });
}

export interface RevenueCatEnsureAppsResult {
  iosAppId?: string;
  androidAppId?: string;
  iosCreated: boolean;
  androidCreated: boolean;
}

export interface RevenueCatCatalogProductRow {
  productId: string;
  type: string;
  displayName?: string;
  subscriptionDuration?: string;
  status: 'synced' | 'missing' | string;
  remoteProductId?: string;
}

export interface RevenueCatCatalogStatus {
  iosAppId?: string;
  androidAppId?: string;
  iosBundleId?: string;
  androidBundleId?: string;
  iosProducts: RevenueCatCatalogProductRow[];
  lastProductSyncAt?: string;
  lastProductSync?: { created: number; skipped: number; failed: number; packagesLinked?: number };
  androidProductSyncReady: boolean;
}

export async function ensureRevenueCatApps(appId: string): Promise<RevenueCatEnsureAppsResult> {
  return apiRequest<RevenueCatEnsureAppsResult>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/apps/ensure`,
    { method: 'POST' }
  );
}

export async function getRevenueCatCatalog(appId: string): Promise<RevenueCatCatalogStatus> {
  const data = await apiRequest<RevenueCatCatalogStatus>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/catalog`
  );
  return {
    ...data,
    iosProducts: Array.isArray(data?.iosProducts) ? data.iosProducts : [],
    androidProductSyncReady: Boolean(data?.androidProductSyncReady),
  };
}

export async function createRevenueCatProductSyncJob(appId: string): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/products/sync`,
    { method: 'POST' }
  );
}

export async function createRevenueCatFullSetupJob(appId: string): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/full-setup`,
    { method: 'POST' }
  );
}

export async function listRemoteRevenueCatPaywalls(appId: string): Promise<RevenueCatRemotePaywall[]> {
  const data = await apiRequest<{ items?: RevenueCatRemotePaywall[] }>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/remote-paywalls`
  );
  return Array.isArray(data?.items) ? data.items : [];
}

function normalizeRevenueCatPaywallSummary(p: RevenueCatPaywallSummary): RevenueCatPaywallSummary {
  return {
    ...p,
    targetLocales: Array.isArray(p?.targetLocales) ? p.targetLocales : [],
    completedTargetLocales: Array.isArray(p?.completedTargetLocales) ? p.completedTargetLocales : [],
  };
}

function normalizeRevenueCatPaywall(p: RevenueCatPaywall): RevenueCatPaywall {
  const entries = Array.isArray(p?.entries) ? p.entries : [];
  return {
    ...p,
    entries: entries.map(e => ({
      ...e,
      // Backend omitempty: alan yoksa default true.
      translate: e?.translate !== false,
    })),
    targetLocales: Array.isArray(p?.targetLocales) ? p.targetLocales : [],
    completedTargetLocales: Array.isArray(p?.completedTargetLocales) ? p.completedTargetLocales : [],
  };
}

export async function listRevenueCatPaywalls(appId: string): Promise<RevenueCatPaywallSummary[]> {
  const data = await apiRequest<{ items?: RevenueCatPaywallSummary[] }>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/paywalls`
  );
  const items = Array.isArray(data?.items) ? data.items : [];
  return items.map(normalizeRevenueCatPaywallSummary);
}

/** RevenueCat'teki aktif paywall(lar)ı hemen senkronize eder (günlük otomatik koşuyu beklemeden). */
export async function syncRevenueCatPaywalls(
  appId: string
): Promise<{ paywallsSynced: number; newPaywalls: number }> {
  return apiRequest<{ paywallsSynced: number; newPaywalls: number }>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/sync`,
    { method: 'POST' }
  );
}

export async function importRevenueCatPaywall(
  appId: string,
  remotePaywallId: string
): Promise<RevenueCatPaywall> {
  const data = await apiRequest<RevenueCatPaywall>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/paywalls`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ remotePaywallId }),
    }
  );
  return normalizeRevenueCatPaywall(data);
}

export async function getRevenueCatPaywall(appId: string, paywallId: string): Promise<RevenueCatPaywall> {
  const data = await apiRequest<RevenueCatPaywall>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/paywalls/${encodeURIComponent(paywallId)}`
  );
  return normalizeRevenueCatPaywall(data);
}

export async function saveRevenueCatPaywall(
  appId: string,
  paywallId: string,
  payload: {
    primaryLocale?: string;
    targetLocales?: string[];
    /** entry.key -> locale -> metin; AI çevirisini elle düzeltmek için. */
    entryTranslations?: Record<string, Record<string, string>>;
    /** entry.key -> translate; false = AI atla, kaynak metni kopyala. */
    entryTranslate?: Record<string, boolean>;
  }
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/paywalls/${encodeURIComponent(paywallId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
}

export async function deleteRevenueCatPaywall(appId: string, paywallId: string): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/paywalls/${encodeURIComponent(paywallId)}`,
    { method: 'DELETE' }
  );
}

export async function createRevenueCatTranslateJob(
  appId: string,
  paywallId: string
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/paywalls/${encodeURIComponent(paywallId)}/translate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }
  );
}

export async function createRevenueCatUploadJob(
  appId: string,
  paywallId: string
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(
    `${API_BASE}/api/v1/applications/${appId}/revenuecat/paywalls/${encodeURIComponent(paywallId)}/upload`,
    { method: 'POST' }
  );
}

// Apple Search Ads — kampanya/keyword performans metrikleri (CPA/CPI, TTR, conversion rate, ROAS).
// Bağlantı HESAP (kullanıcı) seviyesindedir: ASA credentials Search Ads org seviyesinde verilir, tek bir
// Connect o hesaptaki TÜM uygulamaların kampanyalarını görür — uygulama başına ayrı bağlantı gerekmez.
export interface AppleSearchAdsCampaignSummary {
  campaignId: string;
  campaignName: string;
  appName?: string;
  impressions: number;
  taps: number;
  installs: number;
  spend: number;
  currency?: string;
  cpa: number;
  ttr: number;
  cr: number;
}

export interface AppleSearchAdsKeywordSummary {
  campaignId: string;
  campaignName: string;
  appName?: string;
  keywordId: string;
  keyword: string;
  matchType?: string;
  impressions: number;
  taps: number;
  installs: number;
  spend: number;
  cpa: number;
  ttr: number;
  cr: number;
}

export interface AppleSearchAdsSummary {
  connected: boolean;
  /** Yalnızca applicationId ile filtrelenmiş sorgularda anlamlıdır: bu uygulama Apple App Store id'sine
   * eşlenebildi mi (App Store Connect bağlantısı gerektirir). */
  appStoreAppResolved: boolean;
  from: string;
  to: string;
  totalImpressions: number;
  totalTaps: number;
  totalInstalls: number;
  totalSpend: number;
  currency?: string;
  avgCPA: number;
  avgTTR: number;
  avgCR: number;
  campaigns: AppleSearchAdsCampaignSummary[];
  topKeywords: AppleSearchAdsKeywordSummary[];
  revenue?: number;
  roas?: number;
}

export interface AppleSearchAdsDashboardAppSummary {
  applicationId: string;
  applicationName: string;
  adamId?: string;
  spend: number;
  installs: number;
}

export interface AppleSearchAdsDashboardSummary {
  from: string;
  to: string;
  connected: boolean;
  totalImpressions: number;
  totalTaps: number;
  totalInstalls: number;
  totalSpend: number;
  avgCPA: number;
  avgTTR: number;
  avgCR: number;
  apps: AppleSearchAdsDashboardAppSummary[];
}

/** Hesap seviyesinde (uygulamadan bağımsız) Apple Search Ads bağlantısı kurar. */
export async function connectAppleSearchAds(input: {
  clientId: string;
  teamId: string;
  keyId: string;
  privateKey: string;
  orgId?: string;
}): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`${API_BASE}/api/v1/apple-search-ads/connect`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function disconnectAppleSearchAds(): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/apple-search-ads`, { method: 'DELETE' });
}

export async function triggerAppleSearchAdsSync(): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`${API_BASE}/api/v1/apple-search-ads/sync`, { method: 'POST' });
}

/** Hesaptaki tüm kampanyalar (tüm uygulamalar) için toplu özet — panel dashboard kartı. */
export async function getAppleSearchAdsDashboardSummary(): Promise<AppleSearchAdsDashboardSummary> {
  return apiRequest<AppleSearchAdsDashboardSummary>(`${API_BASE}/api/v1/apple-search-ads/summary`);
}

/** Yalnızca bu uygulamayı tanıtan kampanyalarla filtrelenmiş özet. */
export async function getAppleSearchAdsAppSummary(appId: string, days?: number): Promise<AppleSearchAdsSummary> {
  const qs = days ? `?days=${encodeURIComponent(String(days))}` : '';
  return apiRequest<AppleSearchAdsSummary>(`${API_BASE}/api/v1/applications/${appId}/apple-search-ads/summary${qs}`);
}

/** Worker API — job'ı tetikler (job type'a göre worker işlemi başlatır).
 * mode: metadata_translation / screens_ai_translation için full | failed_only (boş = failed varsa yalnızca onlar). */
export async function triggerJob(
  jobId: string,
  opts?: {
    mode?:
      | 'full'
      | 'failed_only'
      | 'render_failed'
      | 'render_failed_upload_failed';
  }
): Promise<{ ok: boolean; jobId: string; type: string }> {
  return apiRequest<{ ok: boolean; jobId: string; type: string }>(`${WORKER_API_BASE}/jobs/trigger`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jobId, ...(opts?.mode ? { mode: opts.mode } : {}) }),
  });
}

/** Worker API — çalışan job process'ini iptal eder (pause sonrası upload/Fastlane kill). */
export async function cancelJob(jobId: string): Promise<{ ok: boolean; canceled: boolean }> {
  return apiRequest<{ ok: boolean; canceled: boolean }>(`${WORKER_API_BASE}/jobs/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jobId }),
  });
}

// Jobs — listeleme, istatistik, pause/start, progress
export type JobStatus = 'pending' | 'processing' | 'paused' | 'completed' | 'failed';

/** Tek AI çağrısının token kullanımı (job.aiUsage). */
export interface JobAIUsage {
  tokensPrompt: number;
  tokensCompletion: number;
  model: string;
  provider?: string;
}

/** Alt işlem (locale vb.) ilerlemesi — worker goroutine'ler için. */
export interface JobSubProgressItem {
  label: string;
  progress: number;
  status: string;
}

export interface Job {
  id: string;
  applicationId: string;
  /** Create anında snapshot; job detail ekstra app çağrısı yapmaz */
  applicationName?: string;
  /** Grup job (örn. publish) altındaysa parent id */
  parentJobId?: string;
  type: string;
  payload: Record<string, unknown>;
  status: JobStatus;
  progress: number;
  subProgress?: JobSubProgressItem[];
  /** Job boyunca AI çağrı usage'ları (job sonunda tek kredi settle) */
  aiUsage?: JobAIUsage[];
  /** ai_translation: locale -> çevrilen metin (API'den job detayda döner) */
  aiPartialResults?: Record<string, string>;
  errorMessage?: string;
  /** Teknik hata detayı — yalnızca DebugMode (goodmode) API yanıtında gelir */
  errorDebug?: string;
  /** Soft uyarı listesi (metadata_upload name-in-use vb.) */
  warnings?: AppWarning[];
  /** metadata_upload: Fastlane konsol çıktısı */
  log?: string;
  /** screens_upload: App Store screenshot yükleme durumu (ok | failed | skipped | uploading) */
  screenshotUploadStatus?: string;
  screenshotUploadError?: string;
  /** screens_upload: deliver'da eksik kalan medya (failed_only retry için) */
  screenshotUploadFailures?: ScreenshotUploadFailure[];
  /** AI kredi tüketim özeti */
  aiCredits?: {
    clientIp?: string;
    userAgent?: string;
    creditsSpent?: number;
    callCount?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ScreenshotUploadFailure {
  locale: string;
  file: string;
  attempts: number;
  lastError?: string;
  skipped?: boolean;
}

/** screens_upload: yeniden denenebilir (skipped olmayan) fail sayısı. */
export function getRetryableScreenshotUploadFailureCount(job: Job): number {
  return (job.screenshotUploadFailures ?? []).filter(
    (f) => !f.skipped && Boolean(f.locale?.trim()) && Boolean(f.file?.trim())
  ).length;
}

/** screens_upload: subProgress'te failed olan locale sayısı (yeniden render seçenekleri için). */
export function getScreensUploadFailedRenderLocaleCount(job: Job): number {
  if (job.type !== 'screens_upload') return 0;
  const fromSub = (job.subProgress ?? [])
    .filter((s) => s.status === 'failed')
    .map((s) => (s.label ?? '').trim())
    .filter(Boolean);
  return new Set(fromSub).size;
}

/**
 * screens_upload: "Upload only" (store retry) kullanılabilir mi?
 * completed, veya render bitmiş / upload fail olmuş failed job.
 */
export function canScreensIOSUploadOnly(job: Job): boolean {
  if (job.type !== 'screens_upload') return false;
  if (job.status === 'completed') return true;
  if (job.status !== 'failed') return false;
  if (getScreensUploadFailedRenderLocaleCount(job) > 0) return false;
  // Upload denendi veya stuck uploading; ya da render dilimi bitti.
  if (job.screenshotUploadStatus) return true;
  if (getRetryableScreenshotUploadFailureCount(job) > 0) return true;
  if ((job.progress ?? 0) >= 85) return true;
  const subs = job.subProgress ?? [];
  return subs.length > 0 && subs.every((s) => s.status === 'completed');
}

/** Locale-bazlı çeviri job'larında (metadata_translation, screens_ai_translation) başarısız alt işlem locale kodları. */
export function getLocaleTranslationFailedLocales(job: Job): string[] {
  if (
    (job.type !== 'metadata_translation' && job.type !== 'screens_ai_translation') ||
    !job.subProgress?.length
  ) {
    return [];
  }
  return job.subProgress
    .filter(s => s.status === 'failed')
    .map(s => (s.label ?? '').trim())
    .filter(Boolean);
}

/** @deprecated Prefer getLocaleTranslationFailedLocales */
export function getMetadataTranslationFailedLocales(job: Job): string[] {
  return getLocaleTranslationFailedLocales(job);
}

/** metadata_translation payload.platforms veya varsayılan iOS. */
export function getMetadataTranslationJobPlatforms(job: Job): string[] {
  const p = job.payload?.platforms;
  if (Array.isArray(p) && p.length > 0) return p.filter((x): x is string => typeof x === 'string' && x.trim() !== '');
  return [PLATFORM_IOS];
}

export interface JobStats {
  total: number;
  pending: number;
  processing: number;
  paused: number;
  completed: number;
  failed: number;
}

export async function listJobs(status?: JobStatus): Promise<Job[]> {
  const url = status
    ? `${API_BASE}/api/v1/jobs?status=${encodeURIComponent(status)}`
    : `${API_BASE}/api/v1/jobs`;
  return apiRequest<Job[]>(url);
}

export async function getJobStats(): Promise<JobStats> {
  return apiRequest<JobStats>(`${API_BASE}/api/v1/jobs/stats`);
}

/** Sistem geneli job yükü (yalnızca DebugMode / goodmode). */
export async function getSystemJobLoad(): Promise<JobStats> {
  return apiRequest<JobStats>(`${API_BASE}/api/v1/system/job-load`);
}

export interface SystemPlanCount {
  planId: string;
  name: string;
  count: number;
  monthlyListPriceUsd: number;
  activePayingCount: number;
}

export interface SystemOverview {
  memberships: {
    total: number;
    byPlan: SystemPlanCount[];
  };
  revenue: {
    currency: string;
    monthlyActiveUsd: number;
    activePaying: number;
  };
  jobs: JobStats;
}

/** Sistem genel bakış: üyelik, gelir ve job özeti (yalnızca DebugMode). */
export async function getSystemOverview(): Promise<SystemOverview> {
  return apiRequest<SystemOverview>(`${API_BASE}/api/v1/system/overview`);
}

export async function getJob(id: string): Promise<Job> {
  return apiRequest<Job>(`${API_BASE}/api/v1/jobs/${id}`);
}

/** Grup job (publish) altındaki alt job'ları döner. */
export async function listJobChildren(parentJobId: string): Promise<Job[]> {
  return apiRequest<Job[]>(`${API_BASE}/api/v1/jobs/${encodeURIComponent(parentJobId)}/children`);
}

/** Job tamamlanana veya fail olana kadar poll eder. */
export async function waitForJob(
  jobId: string,
  opts?: { intervalMs?: number }
): Promise<Job> {
  const intervalMs = opts?.intervalMs ?? 1500;
  for (;;) {
    const job = await getJob(jobId);
    if (job.status === 'completed') return job;
    if (job.status === 'failed') {
      throw new Error(job.errorMessage || 'Job failed');
    }
    await new Promise(r => setTimeout(r, intervalMs));
  }
}

/** Tamamlanmış screens_upload job'ı için App Store screenshot yeniden yükleme; dönen jobId ile triggerJob çağırın.
 * mode: full (tümü) | failed_only (yalnızca kayıtlı fail'ler). */
export async function createScreensIOSStoreUploadJob(
  screensJobId: string,
  mode: 'full' | 'failed_only' = 'full'
): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(`${API_BASE}/api/v1/jobs/${encodeURIComponent(screensJobId)}/ios-screens-upload`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  });
}

/** Bu version için devam eden (pending/processing) AI çeviri job'ını döner; yoksa null. */
export async function getActiveTranslationJob(
  appId: string,
  versionId: string
): Promise<Job | null> {
  const res = await fetch(
    `${API_BASE}/api/v1/applications/${encodeURIComponent(appId)}/versions/${encodeURIComponent(versionId)}/active-translation-job`,
    { headers: authLocaleHeaders() }
  );
  if (res.status === 404) return null;
  if (!res.ok) await throwOnNotOk(res);
  return res.json();
}

/** Job artifact klasörü (locale veya root; multi-lang'da jobId/en-US/, jobId/tr/ vb.). */
export interface JobArtifactFolder {
  name: string;
  files: string[];
}

/** Job artifact listesi — folders (geriye uyumluluk) ve özyinelemeli files (relative path listesi). */
export async function getJobArtifacts(
  jobId: string
): Promise<{ folders: JobArtifactFolder[]; files: string[] }> {
  return apiRequest<{ folders: JobArtifactFolder[]; files: string[] }>(
    `${API_BASE}/api/v1/jobs/${jobId}/artifacts`
  );
}

/** Job artifact dizinini ZIP olarak indirmek için URL. format=ios ile ai_translation job'ları için iOS yapısı (locale.lproj/Localizable.strings) döner. */
export function getJobArtifactArchiveUrl(jobId: string, params?: { format?: string }): string {
  const url = `${API_BASE}/api/v1/jobs/${encodeURIComponent(jobId)}/artifacts/archive`;
  if (params?.format) {
    return `${url}?format=${encodeURIComponent(params.format)}`;
  }
  return url;
}

/** Artifact archive'ı Authorization ile blob olarak indirir (ZIP). format=ios ile ai_translation için iOS yapısında indirir. */
export async function downloadJobArtifactArchive(
  jobId: string,
  filename = 'artifacts.zip',
  params?: { format?: string }
): Promise<void> {
  const url = getJobArtifactArchiveUrl(jobId, params);
  const res = await fetch(url, { headers: authLocaleHeaders() });
  if (!res.ok) await throwOnNotOk(res);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

/** Tek bir job artifact dosyasının URL'i (path: "en-US/screen.jpg" veya "screen.jpg"). Slash encode edilmez ki backend doğru dosya yolunu görsün. */
export function getJobArtifactUrl(jobId: string, path: string): string {
  const pathSegment = path.split('/').map(encodeURIComponent).join('/');
  return `${API_BASE}/api/v1/jobs/${encodeURIComponent(jobId)}/artifacts/${pathSegment}`;
}

/** Artifact dosyasını Authorization ile metin olarak çeker (metadata_export önizleme). */
export async function getJobArtifactAsText(jobId: string, path: string): Promise<string> {
  const url = getJobArtifactUrl(jobId, path);
  const res = await fetch(url, { headers: authLocaleHeaders() });
  if (!res.ok) await throwOnNotOk(res);
  return res.text();
}

/** Artifact dosyasını Authorization ile blob olarak çeker (img önizleme için object URL). */
export async function getJobArtifactAsBlob(jobId: string, path: string): Promise<Blob> {
  const url = getJobArtifactUrl(jobId, path);
  const res = await fetch(url, { headers: authLocaleHeaders() });
  if (!res.ok) await throwOnNotOk(res);
  return res.blob();
}

export async function updateJobStatus(
  id: string,
  payload: { status?: JobStatus; errorMessage?: string; progress?: number }
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`${API_BASE}/api/v1/jobs/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

/** Job'ı siler. */
export async function deleteJob(id: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/jobs/${id}`, {
    method: 'DELETE',
  });
}

/** Seçilen job'ları tek istekte siler. Dönen failedIds silinemeyen id'ler. */
export async function deleteJobsBulk(ids: string[]): Promise<{ deleted: number; failedIds: string[] }> {
  const res = await apiRequest<{ deleted: number; failedIds: string[] | null }>(
    `${API_BASE}/api/v1/jobs/bulk-delete`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    }
  );
  return {
    deleted: res?.deleted ?? 0,
    failedIds: res?.failedIds ?? [],
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Publisher tracking — izlenen rakip App Store yayıncıları + activity timeline
// ─────────────────────────────────────────────────────────────────────────

export interface Publisher {
  id: string;
  userId: string;
  artistId: string;
  displayName: string;
  storefront: string;
  isActive: boolean;
  lastSyncAt?: string;
  lastSyncStatus?: string;
  lastSyncError?: string;
  /** May be omitted when 0 depending on API encoder / older payloads. */
  totalApps?: number;
  lastAppTrackId?: string;
  lastAppName?: string;
  lastAppReleaseDate?: string;
  createdAt: string;
  updatedAt: string;
}

export type PublisherEventType =
  | 'new_app'
  | 'removed_app'
  | 'app_updated'
  | 'rating_changed'
  | 'metadata_changed'
  | 'visual_changed'
  | 'price_changed';

export interface PublisherEvent {
  id: string;
  runId: string;
  publisherId: string;
  publisherName: string;
  appTrackId: string;
  appName: string;
  eventType: PublisherEventType;
  message: string;
  createdAt: string;
}

/** Kullanıcının izlediği yayıncıları döner. */
export async function listPublishers(): Promise<{ items: Publisher[] }> {
  const res = await apiRequest<{ items: Publisher[] }>(`${API_BASE}/api/v1/publishers`);
  return res ?? { items: [] };
}

/** Tek yayıncı detayı. */
export async function getPublisher(id: string): Promise<Publisher> {
  return apiRequest<Publisher>(`${API_BASE}/api/v1/publishers/${id}`);
}

export interface PublisherApp {
  trackId: string;
  trackName: string;
  version?: string;
  primaryGenreName?: string;
  sellerName?: string;
  price: number;
  formattedPrice?: string;
  currency?: string;
  averageUserRating: number;
  userRatingCount: number;
  isRemoved: boolean;
  firstSeenAt: string;
  lastSeenAt: string;
}

/** Yayıncının bilinen App Store uygulamaları (son sync snapshot'ı). */
export async function listPublisherApps(publisherId: string): Promise<{ items: PublisherApp[] }> {
  const res = await apiRequest<{ items: PublisherApp[] }>(
    `${API_BASE}/api/v1/publishers/${publisherId}/apps`
  );
  return res ?? { items: [] };
}

/** App Store uygulama sayfası URL'si (yeni sekmede açmak için). */
export function appStoreAppURL(storefront: string, trackId: string): string {
  const sf = (storefront || 'us').toLowerCase();
  return `https://apps.apple.com/${sf}/app/id${trackId}`;
}

/** App Store developer sayfası URL'si. */
export function appStoreDeveloperURL(storefront: string, artistId: string): string {
  const sf = (storefront || 'us').toLowerCase();
  return `https://apps.apple.com/${sf}/developer/id${artistId}`;
}

/** İzleme listesine yeni bir yayıncı ekler. artistIdOrUrl: developer linki, app linki veya sayısal ID. */
export async function addPublisher(input: {
  artistIdOrUrl: string;
  displayName?: string;
  country?: string;
}): Promise<Publisher> {
  return apiRequest<Publisher>(`${API_BASE}/api/v1/publishers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

/** Yayıncıyı izleme listesinden kaldırır. */
export async function removePublisher(id: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/publishers/${id}`, { method: 'DELETE' });
}

/** Manuel "şimdi senkronize et" için pending job oluşturur; dönen jobId ile triggerJob çağrılmalı. */
export async function createPublisherSyncJob(publisherId: string): Promise<{ jobId: string }> {
  return apiRequest<{ jobId: string }>(`${API_BASE}/api/v1/publishers/${publisherId}/sync-jobs`, {
    method: 'POST',
  });
}

function publisherEventsQuery(params?: { before?: string; limit?: number }): string {
  const q = new URLSearchParams();
  if (params?.before) q.set('before', params.before);
  if (params?.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

/** Yayıncı activity timeline'ı; cursor pagination (before: son öğenin createdAt'i).
 * Backend omits noisy types (app_updated, rating_changed). */
export async function getPublisherEvents(params?: { before?: string; limit?: number }): Promise<{ items: PublisherEvent[] }> {
  const res = await apiRequest<{ items: PublisherEvent[] }>(
    `${API_BASE}/api/v1/publishers/events${publisherEventsQuery(params)}`
  );
  return res ?? { items: [] };
}

/** Tek yayıncıya ait uygulama güncelleme timeline'ı (app_updated dahil). */
export async function getPublisherEventsForPublisher(
  publisherId: string,
  params?: { before?: string; limit?: number }
): Promise<{ items: PublisherEvent[] }> {
  const res = await apiRequest<{ items: PublisherEvent[] }>(
    `${API_BASE}/api/v1/publishers/${publisherId}/events${publisherEventsQuery(params)}`
  );
  return res ?? { items: [] };
}

/* ── ASO — Apple Ads Platform API v1 search term popularity ─────────────────────
 * Pazar seviyesi keyword verisi (ülke + genre bazlı), kullanıcıya özel değildir.
 * Trend/delta/sparkline değerleri backend'de hesaplanır; UI hazır değer render eder.
 */

/** Keşif tablosunun tek satırı — tüm trend alanları backend'den hazır gelir. */
export interface ASOTermRow {
  searchTerm: string;
  genre: string;
  rankInGenre: number;
  popularity: number;
  popularityInGenre: number;
  popularity1to5: number;
  popularityDelta: number;
  rankDelta: number;
  isNew: boolean;
  sparkline: number[];
  inMetadata?: boolean;
  fit?: 'missed' | 'working' | 'dead_weight';
}

export interface ASOExploreResponse {
  granularity: string;
  period: string;
  previousPeriod?: string;
  countryOrRegion: string;
  tier?: string;
  genre: string;
  segment: string;
  rows: ASOTermRow[];
  counts: Record<string, number>;
}

export interface ASOFiltersResponse {
  granularity: string;
  countries: string[];
  genres: string[];
  periods: string[];
  tiers: Record<string, string[]>;
}

export interface ASOTimelinePoint {
  period: string;
  periodStart: string;
  genre: string;
  rankInGenre: number;
  searchPopularityInGenre: number;
  searchPopularity1to100: number;
  searchPopularity1to5: number;
}

export interface ASOTimelineResponse {
  searchTerm: string;
  countryOrRegion: string;
  tier?: string;
  granularity: string;
  points: ASOTimelinePoint[];
}

export interface ASOSyncRun {
  id: string;
  triggeredBy: string;
  startedAt: string;
  finishedAt?: string;
  status: string;
  granularity: string;
  period?: string;
  countriesRequested: number;
  countriesCollected: number;
  countriesSkipped: number;
  rowsSynced: number;
  errorMessage?: string;
  /** Stable key (e.g. "aso.noCredentials") for UI branching — never for display, use errorMessage for that. */
  errorCode?: string;
}

export interface ASOCoverageResponse {
  locale: string;
  countryOrRegion: string;
  tier?: string;
  genre: string;
  genreAutoResolved: boolean;
  availableGenres: string[];
  granularity: string;
  period: string;
  keywordCount: number;
  keywordLimit: number;
  keywordCharCount: number;
  keywordCharLimit: number;
  coveredCount: number;
  missed: ASOTermRow[];
  deadWeight: ASOTermRow[];
  working: ASOTermRow[];
}

export interface ASOKeywordSuggestion {
  id: string;
  applicationId: string;
  locale: string;
  searchTerm: string;
  countryOrRegion: string;
  genre?: string;
  popularity: number;
  rankInGenre: number;
  status: string;
  createdAt: string;
}

export interface ASOBasketResponse {
  locale: string;
  items: ASOKeywordSuggestion[];
  currentKeywords: string[];
  resultingCount: number;
  resultingCharCount: number;
  keywordLimit: number;
  keywordCharLimit: number;
  exceedsLimit: boolean;
}

/** Keşif ekranının ana sorgusu: seçili ülke+genre+dönem için trend'li terim listesi. */
export async function getASOExplore(params: {
  country: string;
  genre: string;
  segment?: string;
  period?: string;
  granularity?: string;
  limit?: number;
}): Promise<ASOExploreResponse> {
  const qs = new URLSearchParams();
  qs.set('country', params.country);
  qs.set('genre', params.genre);
  if (params.segment) qs.set('segment', params.segment);
  if (params.period) qs.set('period', params.period);
  if (params.granularity) qs.set('granularity', params.granularity);
  if (params.limit) qs.set('limit', String(params.limit));
  return apiRequest<ASOExploreResponse>(`${API_BASE}/api/v1/aso/explore?${qs.toString()}`);
}

/** Filtre çubuğunu besleyen ülke/genre/dönem listeleri — toplanan veriden türetilir. */
export async function getASOFilters(params?: { country?: string; granularity?: string }): Promise<ASOFiltersResponse> {
  const qs = new URLSearchParams();
  if (params?.country) qs.set('country', params.country);
  if (params?.granularity) qs.set('granularity', params.granularity);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest<ASOFiltersResponse>(`${API_BASE}/api/v1/aso/filters${suffix}`);
}

/** Tek bir terimin bir ülkedeki popülerlik seyri (detay paneli grafiği). */
export async function getASOTimeline(params: {
  country: string;
  term: string;
  genre?: string;
  granularity?: string;
  periods?: number;
}): Promise<ASOTimelineResponse> {
  const qs = new URLSearchParams();
  qs.set('country', params.country);
  qs.set('term', params.term);
  if (params.genre) qs.set('genre', params.genre);
  if (params.granularity) qs.set('granularity', params.granularity);
  if (params.periods) qs.set('periods', String(params.periods));
  return apiRequest<ASOTimelineResponse>(`${API_BASE}/api/v1/aso/keywords/timeline?${qs.toString()}`);
}

export async function triggerASOSync(): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`${API_BASE}/api/v1/aso/sync`, { method: 'POST' });
}

export async function getASORuns(limit?: number): Promise<{ runs: ASOSyncRun[] }> {
  const qs = limit ? `?limit=${encodeURIComponent(String(limit))}` : '';
  return apiRequest<{ runs: ASOSyncRun[] }>(`${API_BASE}/api/v1/aso/runs${qs}`);
}

/** Uygulamanın keyword'lerini pazar verisiyle karşılaştıran gap analizi. */
export async function getASOAppCoverage(
  appId: string,
  params?: { locale?: string; genre?: string; versionId?: string; limit?: number }
): Promise<ASOCoverageResponse> {
  const qs = new URLSearchParams();
  if (params?.locale) qs.set('locale', params.locale);
  if (params?.genre) qs.set('genre', params.genre);
  if (params?.versionId) qs.set('versionId', params.versionId);
  if (params?.limit) qs.set('limit', String(params.limit));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest<ASOCoverageResponse>(`${API_BASE}/api/v1/applications/${appId}/aso/coverage${suffix}`);
}

export async function getASOBasket(
  appId: string,
  params?: { locale?: string; versionId?: string }
): Promise<ASOBasketResponse> {
  const qs = new URLSearchParams();
  if (params?.locale) qs.set('locale', params.locale);
  if (params?.versionId) qs.set('versionId', params.versionId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiRequest<ASOBasketResponse>(`${API_BASE}/api/v1/applications/${appId}/aso/basket${suffix}`);
}

export async function addASOKeywordToBasket(
  appId: string,
  input: {
    locale: string;
    searchTerm: string;
    countryOrRegion?: string;
    genre?: string;
    popularity?: number;
    rankInGenre?: number;
  }
): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>(`${API_BASE}/api/v1/applications/${appId}/aso/basket`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function removeASOKeywordFromBasket(appId: string, suggestionId: string): Promise<void> {
  await apiRequest<void>(`${API_BASE}/api/v1/applications/${appId}/aso/basket/${suggestionId}`, {
    method: 'DELETE',
  });
}

/** Sepeti versiyonun keyword alanına yazar; limit aşılıyorsa backend hata döner ve hiçbir şey yazılmaz. */
export async function applyASOBasket(
  appId: string,
  input?: { locale?: string; versionId?: string }
): Promise<ASOBasketResponse> {
  return apiRequest<ASOBasketResponse>(`${API_BASE}/api/v1/applications/${appId}/aso/basket/apply`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input ?? {}),
  });
}
